// @flow

import styled from '@emotion/styled'
import * as Sentry from '@sentry/gatsby'
import { useFormik } from 'formik'
import { Link } from 'gatsby'
import type { PageProps } from 'gatsby'
import { StaticImage } from 'gatsby-plugin-image'
import * as React from 'react'

import { ssoStorage, useAuth } from '@toggl/auth'
import { colors, fonts, media } from '@toggl/style'
import { Button, Form, Hero, Input, Link as UiLink, text } from '@toggl/ui'
import { LayoutTrack } from 'components/layouts'

declare var process: WebProcess

const validate = (values) => {
  const errors = {}

  if (!values.email) {
    errors.email = 'Required'
  } else if (!/\S+@\S+\.\S+/.test(values.email)) {
    errors.email = 'Invalid email address'
  }

  return errors
}

const getSsoErrorMsg = (params) => {
  const msg =
    'Single Sign On is not configured for your email address. Please try a different login method, or contact your administrator.'
  const value =
    params.get('ssoError') || params.get('sso_error') || params.get('error')
  return !!value && value !== 'false' ? msg : ''
}
const SsoLoginPage = ({ location }: PageProps<>) => {
  const params = new URLSearchParams(location.search)
  const client = params.get('fromTogglButton') ? 'toggl-button' : 'webapp'
  const email = params.get('email')
  const finish = params.get('finish') || params.get('success')
  const [ready, auth] = useAuth()
  const formik = useFormik<{ email: string }>({
    initialValues: {
      email: email || '',
    },
    validate,
    validateOnChange: false,
    initialStatus: getSsoErrorMsg(params),
    onSubmit: async ({ email }) => {
      try {
        const sso_url = await auth.ssoBegin({
          client,
          email: encodeURIComponent(email),
        })
        ssoStorage.setSsoData({ client, email })
        window.location.assign(sso_url)
      } catch (e) {
        formik.setSubmitting(false)
        Sentry.captureException(e, { contexts: { LoginErrorDetails: e.meta } })
        formik.setStatus(e.message)
      }
    },
  })

  React.useEffect(() => {
    if (client === 'toggl-button' && finish) {
      window.postMessage(
        {
          direction: 'from-public-web',
          message: 'login-success',
        },
        '*'
      )
    }
  }, [client, finish])

  return (
    <LayoutTrack title={'SSO Login | Toggl Track'}>
      <Hero.Wrapper
        bgImage={
          <StaticImage
            formats={['auto', 'webp', 'avif']}
            src='../../assets/hero-bg-track.jpg'
            loading='eager'
            alt='Hero background image'
            layout='fullWidth'
            placeholder='none'
            objectFit='cover'
            backgroundColor='black'
          />
        }
        bgVideo={require('assets/hero-bg-video-track.mp4').default}
        solidColor='black'
      >
        <StyledWrapper>
          <Hero.Title centered>
            <Rotalic>SSO</Rotalic> Login
          </Hero.Title>
          <SmallTitle>Get Tracking</SmallTitle>
        </StyledWrapper>
      </Hero.Wrapper>
      <Root>
        <StyledForm error={formik.status} onSubmit={formik.handleSubmit}>
          <Input
            id='email'
            label={<label htmlFor='email'>Email</label>}
            name='email'
            type='email'
            placeholder={'Email'}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            value={formik.values.email}
            error={
              formik.errors.email && formik.touched.email ? (
                <div>{formik.errors.email}</div>
              ) : null
            }
          />

          <ForgotPasswordWrapper>
            <Link to='/track/login/'>
              <UiLink.Underlined>
                Log in with a different method
              </UiLink.Underlined>
            </Link>
          </ForgotPasswordWrapper>

          <Button.Primary
            type='submit'
            disabled={!ready || formik.isSubmitting}
          >
            Log in
          </Button.Primary>
        </StyledForm>

        <SignupCta>
          <span>Don't have an account?</span>
          <Link to={'/track/signup/'}>
            <Button.Primary>Sign up for free</Button.Primary>
          </Link>
        </SignupCta>
      </Root>
    </LayoutTrack>
  )
}
const Rotalic = styled.span`
  font-family: ${fonts.gtHaptikMediumRotalic};
  color: ${(p) => p.theme.primary};
`

const StyledWrapper = styled(Hero.ContentWrapper)`
  ${media.mq[1]} {
    padding: 76px 20px 168px 20px;
  }
`
const Root = styled.div`
  padding-bottom: 130px;
  background: ${colors.darkPurple90};
`
const SignupCta = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;

  span {
    ${text.paragraph2};
    color: ${colors.orange10};
    margin-bottom: 30px;
  }
`
const SmallTitle = styled.div`
  ${text.paragraph2};
  color: ${colors.orange10};
  text-align: center;
  margin-bottom: 15px;

  ${media.mq[1]} {
    margin-bottom: 30px;
  }
`

const StyledForm = styled(Form)`
  padding: 30px;
  background-color: ${colors.darkPurple100};
  margin-bottom: 40px;
  ${media.mq[1]} {
    width: 673px;
    padding: 58px 45px 64px 45px;
    margin: 0 auto;
    transform: translateY(-100px);
    z-index: 10000;
    position: relative;
  }
`

const ForgotPasswordWrapper = styled.div`
  text-align: right;
  margin-top: 15px;
`

export default SsoLoginPage
